/**
 * Built-in `cctv-stream` widget — a `<video>` shell.
 *
 * v1 handles `mp4` (direct src) and `hls` (Safari native; other browsers
 * load hls.js from a CDN on demand). `webrtc` shows a placeholder until
 * signalling is wired up. On base-portable SITE builds hls.js is served from
 * the site's own bundle instead (zero public-CDN egress) — see ../../vendor.
 */
import type { DashboardWidget, DashboardWidgetInstance, DashboardCtx } from '../../Widget';
import { vendorLibUrl } from '../../vendor';

export type CctvStreamType = 'hls' | 'mp4' | 'webrtc';

export interface CctvStreamConfig {
  src: string;
  type: CctvStreamType;
  muted?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  label?: string;
}

/** Runtime state — only the things that aren't authored config. */
interface CctvStreamState {
  currentTime?: number;
}

const HLS_JS_URL = vendorLibUrl(
  'hls-1.5.13.min.js',
  'https://cdn.jsdelivr.net/npm/hls.js@1.5.13/dist/hls.min.js',
);
let hlsLoader: Promise<any> | null = null;

function loadHlsJs(): Promise<any> {
  if ((window as any).Hls) return Promise.resolve((window as any).Hls);
  if (!hlsLoader) {
    hlsLoader = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = HLS_JS_URL;
      s.async = true;
      s.onload = () => resolve((window as any).Hls);
      s.onerror = () => reject(new Error('hls.js load failed'));
      document.head.appendChild(s);
    });
  }
  return hlsLoader;
}

function describeMediaError(e: MediaError | null): string {
  if (!e) return 'unknown';
  switch (e.code) {
    case 1: return 'aborted';
    case 2: return 'network';
    case 3: return 'decode';
    case 4: return 'src not supported (wrong type? blocked by CORS/mixed-content?)';
    default: return `code ${e.code}`;
  }
}

class CctvStreamInstance implements DashboardWidgetInstance {
  private host: HTMLElement;
  private config: CctvStreamConfig;
  private videoEl: HTMLVideoElement | null = null;
  private hls: any = null;

  constructor(host: HTMLElement, config: CctvStreamConfig, _ctx: DashboardCtx) {
    this.host = host;
    this.config = config;
    this.render();
  }

  private placeholder(text: string): void {
    const div = document.createElement('div');
    div.style.cssText =
      'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#7d8a96;font-size:12px;text-align:center;padding:8px';
    div.textContent = text;
    this.host.appendChild(div);
  }

  private retryTimer: number | undefined;

  /** A camera source coming and going must not leave a dead red banner: show
   *  the reason softly and re-render every 10s until the stream is back —
   *  the successful render replaces everything, ending the cycle. */
  private scheduleRetry(text: string): void {
    this.showError(`${text}\nretrying in 10s…`);
    if (this.retryTimer !== undefined) window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = undefined;
      this.render();
    }, 10000);
  }

  private showError(text: string): void {
    let banner = this.host.querySelector('.cctv-err') as HTMLElement | null;
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'cctv-err';
      banner.style.cssText =
        'position:absolute;left:0;right:0;bottom:0;z-index:5;background:rgba(80,16,16,0.92);color:#ffdada;font-size:11px;padding:6px 8px;font-family:ui-monospace,Menlo,monospace;line-height:1.35;word-break:break-word';
      this.host.appendChild(banner);
    }
    banner.textContent = text;
  }

  private teardownHls(): void {
    if (this.hls) {
      try { this.hls.destroy(); } catch { /* ignore */ }
      this.hls = null;
    }
  }

  private render(): void {
    this.teardownHls();
    this.host.innerHTML = '';

    if (this.config.type === 'webrtc') {
      this.placeholder('WebRTC requires signalling — not yet implemented');
      return;
    }

    if (!this.config.src) {
      this.placeholder('No source URL — open Configure to set one');
      return;
    }

    const v = document.createElement('video');
    v.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;background:#000;object-fit:contain';
    v.muted = this.config.muted ?? true;
    v.autoplay = this.config.autoplay ?? true;
    v.loop = this.config.loop ?? false;
    v.playsInline = true;
    v.controls = true;
    v.addEventListener('error', () => {
      // When hls.js drives this element (MSE), raw element errors during a
      // source drop produce the misleading "src not supported" text — hls.js
      // reports its own errors, so stay quiet here on that path.
      if (this.hls) return;
      // Native-HLS path: an element error on a live stream is almost always
      // the SOURCE being away (camera off, mediamtx 404), not a codec issue —
      // say that, instead of the "src not supported (CORS?)" guesswork.
      const isHlsSrc = /\.m3u8(\?|$)/i.test(src);
      const reason = describeMediaError(v.error);
      this.scheduleRetry(isHlsSrc
        ? `stream offline — camera not publishing?\n${src}`
        : `stream error (${reason})\n${src}`);
    });
    this.videoEl = v;
    this.host.appendChild(v);

    const src = this.config.src;
    const isHls = this.config.type === 'hls' || /\.m3u8(\?|$)/i.test(src);
    const canNativeHls = v.canPlayType('application/vnd.apple.mpegurl') !== '';

    if (isHls && !canNativeHls) {
      loadHlsJs().then((Hls) => {
        if (this.videoEl !== v) return;
        if (Hls?.isSupported?.()) {
          // withCredentials: the site's HLS door sits behind the login wall,
          // and hls.js fetches cookieless by default — every playlist request
          // answered 401 and the cell said "stream error" while the operator
          // was logged in (found live 2026-08-25). Same-origin cookies ride
          // along now; a keyless standalone box is unaffected.
          this.hls = new Hls({
            xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; },
            fetchSetup: (context: any, initParams: any) => {
              initParams.credentials = 'include';
              return new Request(context.url, initParams);
            },
          });
          this.hls.on(Hls.Events.ERROR, (_e: any, data: any) => {
            if (!data?.fatal) return;
            const offline = /manifest|level.*load|frag.*load/i.test(String(data.details ?? ''));
            this.scheduleRetry(offline
              ? `stream offline — camera not publishing?\n${src}`
              : `hls error (${data.type}/${data.details})\n${src}`);
          });
          this.hls.loadSource(src);
          this.hls.attachMedia(v);
        } else {
          this.showError(`hls.js unsupported in this browser`);
        }
      }).catch((err) => {
        this.showError(`hls.js load failed: ${err.message}`);
      });
    } else {
      v.src = src;
    }
  }

  renderConfigUI(host: HTMLElement, onChange: (cfg: unknown) => void): void {
    host.innerHTML = '';
    const form = document.createElement('div');
    form.style.cssText = 'display:flex;flex-direction:column;gap:6px;font-size:12px';

    const mk = (label: string, input: HTMLElement): HTMLElement => {
      const row = document.createElement('label');
      row.style.cssText = 'display:flex;flex-direction:column;gap:2px';
      row.textContent = label;
      row.appendChild(input);
      return row;
    };

    const srcIn = document.createElement('input');
    srcIn.type = 'text';
    srcIn.value = this.config.src ?? '';
    srcIn.placeholder = 'https://…/stream.m3u8 or .mp4';

    const typeIn = document.createElement('select');
    for (const t of ['hls', 'mp4', 'webrtc'] as const) {
      const o = document.createElement('option');
      o.value = t;
      o.textContent = t;
      if (this.config.type === t) o.selected = true;
      typeIn.appendChild(o);
    }

    const labelIn = document.createElement('input');
    labelIn.type = 'text';
    labelIn.value = this.config.label ?? '';

    const loopIn = document.createElement('input');
    loopIn.type = 'checkbox';
    loopIn.checked = this.config.loop ?? false;

    const mutedIn = document.createElement('input');
    mutedIn.type = 'checkbox';
    mutedIn.checked = this.config.muted ?? true;

    const autoplayIn = document.createElement('input');
    autoplayIn.type = 'checkbox';
    autoplayIn.checked = this.config.autoplay ?? true;

    const emit = (): void => {
      onChange({
        src: srcIn.value.trim(),
        type: typeIn.value as CctvStreamType,
        label: labelIn.value || undefined,
        loop: loopIn.checked,
        muted: mutedIn.checked,
        autoplay: autoplayIn.checked,
      } satisfies CctvStreamConfig);
    };
    srcIn.addEventListener('change', emit);
    typeIn.addEventListener('change', emit);
    labelIn.addEventListener('change', emit);
    loopIn.addEventListener('change', emit);
    mutedIn.addEventListener('change', emit);
    autoplayIn.addEventListener('change', emit);
    srcIn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); emit(); }
    });

    // Inline checkbox row helper — label sits to the right of the box.
    const mkCheck = (text: string, input: HTMLInputElement): HTMLElement => {
      const row = document.createElement('label');
      row.style.cssText = 'display:flex;align-items:center;gap:6px;font-size:12px';
      row.appendChild(input);
      const span = document.createElement('span');
      span.textContent = text;
      row.appendChild(span);
      return row;
    };

    form.appendChild(mk('Source URL', srcIn));
    form.appendChild(mk('Type', typeIn));
    form.appendChild(mk('Label', labelIn));
    form.appendChild(mkCheck('Loop', loopIn));
    form.appendChild(mkCheck('Muted', mutedIn));
    form.appendChild(mkCheck('Autoplay', autoplayIn));
    host.appendChild(form);
  }

  getState(): unknown {
    // State is runtime-only — config is owned by `cell.config` and would
    // otherwise clobber a freshly-applied config when the dashboard's
    // rebuildCell flow runs applyState() right after mount() on the new
    // instance. We persist just the playhead position so a reload (or a
    // Configure-driven Apply) resumes near where the user was watching.
    const t = this.videoEl?.currentTime;
    if (typeof t !== 'number' || !isFinite(t) || t <= 0) return {};
    const state: CctvStreamState = { currentTime: t };
    return state;
  }

  applyState(s: unknown): void {
    if (!s || typeof s !== 'object') return;
    const state = s as CctvStreamState;
    if (typeof state.currentTime === 'number' && isFinite(state.currentTime) && this.videoEl) {
      const v = this.videoEl;
      const seek = () => { try { v.currentTime = state.currentTime!; } catch { /* ignore */ } };
      // <video> can't seek before metadata loads; queue the seek for then.
      if (v.readyState >= 1) seek();
      else v.addEventListener('loadedmetadata', seek, { once: true });
    }
  }

  private resumeOnShow = false;

  /** Hidden tab: pause the decode; shown: resume if it was playing. */
  setVisible(visible: boolean): void {
    const v = this.videoEl;
    if (!v) return;
    if (!visible) {
      this.resumeOnShow = !v.paused;
      v.pause();
    } else if (this.resumeOnShow) {
      this.resumeOnShow = false;
      void v.play().catch(() => { /* autoplay refused — the user can press play */ });
    }
  }

  dispose(): void {
    if (this.retryTimer !== undefined) window.clearTimeout(this.retryTimer);
    this.teardownHls();
    if (this.videoEl) {
      try {
        this.videoEl.pause();
        this.videoEl.removeAttribute('src');
        this.videoEl.load();
      } catch {
        /* ignore */
      }
      this.videoEl = null;
    }
    this.host.innerHTML = '';
  }
}

export const cctvStreamWidget: DashboardWidget = {
  id: 'cctv-stream',
  label: 'CCTV stream',
  description: 'Plays an HLS, MP4, or (eventually) WebRTC video feed.',
  mount(host, config, ctx) {
    return new CctvStreamInstance(host, (config ?? { src: '', type: 'mp4' }) as CctvStreamConfig, ctx);
  },
};
